import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';

import {
  Card, BlockStack, InlineStack, Text, Button, Tabs, IndexTable,
  Badge, Banner, Modal,
} from '@shopify/polaris';
import { PlusIcon, EditIcon, DeleteIcon } from '@shopify/polaris-icons';

import { packingRulesIndex, packingRuleDelete } from '../../redux/actions/packingRules';
import { PACKING_RULE_TYPE_LABELS as RULE_TYPE_LABELS, PACKING_RULE_LEVEL_LABELS as LEVEL_LABELS } from '../../constants';

const TABS = [
  { id: 'all', content: 'All' },
  { id: 'variant', content: 'Variants' },
  { id: 'product', content: 'Products' },
  { id: 'collection', content: 'Collections' },
  { id: 'tag', content: 'Tags' },
  { id: 'order', content: 'Order shipments' }
];

const Rules = ({ packingRules, loadPackingRules, deletePackingRule }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  useEffect(() => {
    loadPackingRules();
  }, [loadPackingRules]);

  const filteredRules = () => {
    let rules = packingRules.packingRules || [];
    const tabLevel = TABS[activeTab].id;

    if (tabLevel === 'all') {
      return rules;
    }

    return rules.filter(rule => rule.level === tabLevel);
  }

  const targetText = (rule) => {
    if (rule.level === 'order') {
      return 'Every shipment';
    }

    // Rules created through the catalog pickers carry the target's name
    // so the list reads like a catalog, not an id dump
    if (rule.product_name) {
      let text = rule.product_name;
      if (rule.level === 'variant' && rule.variant_options?.length > 0) {
        text += ` (${rule.variant_options.map(option => `${option.option_display_name}: ${option.label}`).join(' / ')})`;
      }
      return text;
    }

    if ((rule.level === 'collection' || rule.level === 'tag' || rule.level === 'category') && rule.grouping_name) {
      return rule.grouping_name;
    }

    if (rule.level === 'variant') {
      let [productId, variantId] = (rule.target_id || '').split('#');
      return `Product ${productId}, Variant ${variantId}`;
    }

    return `${LEVEL_LABELS[rule.level] || rule.level} ${rule.target_id}`;
  }

  const levelBadge = (rule) => {
    if (rule.level === 'order') {
      return <Badge>Order checklist</Badge>;
    }

    return rule.shipment_level
      ? <Badge>Order checklist</Badge>
      : <Badge tone="success">Item checklist</Badge>;
  }

  const actionButtons = (rule) => {
    return(
      <InlineStack gap="200">
        <Link to={`/rules/edit/${rule.id}`}>
          <Button
            icon={EditIcon}
            disabled={packingRules.isUpdating}
          >
            Edit
          </Button>
        </Link>
        <Button
          onClick={() => setRuleToDelete(rule)}
          tone="critical"
          icon={DeleteIcon}
          disabled={packingRules.isUpdating}
        >
          Delete
        </Button>
      </InlineStack>
    )
  }

  const ruleRow = (rule) => {
    return {
      ...rule,
      type_label: RULE_TYPE_LABELS[rule.rule_type] || rule.rule_type,
      target_text: targetText(rule)
    }
  }

  const rows = filteredRules().map(ruleRow);

  return (
    <>
      <SettingsPage
        title="Packing Rules"
        description="Packing rules build the checklists packers see in the warehouse app. Variant, product, collection and tag rules normally appear on the matching item's checklist; enabling 'Applies to the whole shipment' moves them to the order checklist instead. Order shipment rules apply to every shipment. Have questions or need some help? Contact us at support@athousandapps.com"
      >
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">Packing Rules</Text>

            { packingRules.status === 'success' &&
              <>
                <Tabs
                  tabs={TABS}
                  selected={activeTab}
                  onSelect={setActiveTab}
                />

                { rows.length > 0 &&
                  <IndexTable
                    resourceName={{ singular: 'rule', plural: 'rules' }}
                    itemCount={rows.length}
                    selectable={false}
                    headings={[
                      { title: 'Name' },
                      { title: 'Type' },
                      { title: 'Target' },
                      { title: 'Checklist' },
                      { title: 'Actions' },
                    ]}
                  >
                    {rows.map((rule, index) => (
                      <IndexTable.Row id={rule.id} key={rule.id} position={index}>
                        <IndexTable.Cell>{rule.name}</IndexTable.Cell>
                        <IndexTable.Cell>{rule.type_label}</IndexTable.Cell>
                        <IndexTable.Cell>{rule.target_text}</IndexTable.Cell>
                        <IndexTable.Cell>{levelBadge(rule)}</IndexTable.Cell>
                        <IndexTable.Cell>{actionButtons(rule)}</IndexTable.Cell>
                      </IndexTable.Row>
                    ))}
                  </IndexTable>
                }

                { rows.length === 0 &&
                  <Banner tone="warning">
                    <p>No packing rules in this section yet.</p>
                  </Banner>
                }

                <InlineStack align="start">
                  <Link to="/rules/new">
                    <Button variant="primary" icon={PlusIcon}>
                      New Packing Rule
                    </Button>
                  </Link>
                </InlineStack>
              </>
            }

            { packingRules.status !== 'success' &&
              <LoadingSpinner size="small" statusText="Loading packing rules"/>
            }
          </BlockStack>
        </Card>
      </SettingsPage>

      <Modal
        open={ruleToDelete !== null}
        onClose={() => setRuleToDelete(null)}
        title="Delete Packing Rule"
        primaryAction={{
          content: 'Delete Rule',
          destructive: true,
          onAction: () => { deletePackingRule(ruleToDelete.id); setRuleToDelete(null); },
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setRuleToDelete(null),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">{ruleToDelete?.name}</Text>
            <Text as="p">{ruleToDelete?.description}</Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  )
}

const mapStateToProps = state => ({
  packingRules: state.packingRules,
});

const mapDispatchToProps = (dispatch) => ({
  loadPackingRules: () => dispatch(packingRulesIndex()),
  deletePackingRule: (id) => dispatch(packingRuleDelete(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Rules);
